/* eslint-disable import/prefer-default-export */
import { concat, map, compact, chain, unionBy, isEmpty } from 'lodash'
import type { SearchResult } from '@src/redux/reducers/autocomplete.query'
import type {
  SearchResultTableRow,
  SearchResultTableRow_UsedActionsEntry,
  SearchResultTableRow_TotalActionsEntry
} from '@src/endpoints/services/commonHdr.service'
import { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import { AccountInfo } from '@src/endpoints/services/base.service'
import { getAccountNameById } from './accounts'
import { getRegionFromUri, getIdFromUri } from './url.utils'
import type { ResourceTypeDict } from '@src/redux/reducers/shell'
import { parsePolicies, getCountPolicies } from './policy.utils'
import type { AutocompleteTable } from './redux.tools'

export interface HumanNamesResponse {
  Name?: string
  URI?: string
}

export interface ResourceInfo {
  RegionName?: string[]
  Name?: string[]
  VpcID?: string[]
  SubnetID?: string[]
  IPAddress?: string[]
  PublicIPAddress?: string[]
  SecurityGroups?: string[]
  Tags?: string[]
  CidrBlock?: string[]
  OpenPorts?: string[]
  Type?: string[]
  CloudType?: string[]
  Schema?: string[]
  ViewQuery?: string[]
  DatasetURI?: string[]
  UnknownAccount?: boolean[]
  Tables?: string[]
  ObjectCount?: number
  Objects?: string[]
  S3Acl?: string[]
  KMSKeys?: string[]
  IPPermissions?: string[]
  IPPermissionsEgress?: string[]
}

export function parseActions(item: SearchResult | AutocompleteTable = {}) {
  if (isEmpty(item?.actions)) {
    return null
  }
  let actions = ''
  actions += (item?.actions || []).includes('READ') ? 'R' : '-'
  actions += (item?.actions || []).includes('WRITE') ? 'W' : '-'
  actions += (item?.actions || []).includes('ADMIN') ? 'A' : '-'
  return actions
}

export function parseToInfoDetails(
  resources: string[] = [],
  humanNames: HumanNamesResponse = {}
) {
  return resources.map(
    itemUri =>
      humanNames[itemUri] || { name: getIdFromUri(itemUri), uri: itemUri }
  )
}

export function parseInfoDetails(
  parsedResponse: ResourceInfo,
  humanNames: HumanNamesResponse = {}
) {
  return {
    ...parsedResponse,
    VPC: parseToInfoDetails(parsedResponse?.VpcID, humanNames),
    Subnets: parseToInfoDetails(parsedResponse?.SubnetID, humanNames),
    IPAddresses: parsedResponse?.IPAddress,
    PublicIPAddress: parsedResponse?.PublicIPAddress,
    SecurityGroups: parseToInfoDetails(
      parsedResponse?.SecurityGroups,
      humanNames
    ),
    Tags: parsedResponse?.Tags
  }
}

export type InfoDetailsResult = Partial<ReturnType<typeof parseInfoDetails>>

export interface ActionsFormatOptions<T> {
  readKey: keyof T
  writeKey: keyof T
  adminKey: keyof T
}
type ActionsFormatData<T> = { [key in keyof T]?: T[keyof T] }
export const getActionsFormat = <T extends ActionsFormatData<T>>(
  data: T,
  options: ActionsFormatOptions<T>
) => {
  let actions = ''
  const read = data[options?.readKey] && data[options?.readKey]
  const write = data[options?.writeKey] && data[options?.writeKey]
  const admin = data[options?.adminKey] && data[options?.adminKey]
  if (!read && !write && !admin) {
    return null
  }
  actions += read > 0 ? 'R' : '-'
  actions += write > 0 ? 'W' : '-'
  actions += admin > 0 ? 'A' : '-'
  return actions
}

export function parseActionsFromObject(
  obj:
    | SearchResultTableRow_UsedActionsEntry
    | SearchResultTableRow_TotalActionsEntry = {}
) {
  return getActionsFormat(obj, {
    readKey: 'READ',
    writeKey: 'WRITE',
    adminKey: 'ADMIN'
  })
}

export const parseCommonResourceFields = (items: SearchResultTableRow[]) => {
  const mapTotalActions = compact(map(items, 'total_actions'))
  const mapUsedActions = compact(map(items, 'used_actions'))
  const mapPolicies = compact(map(items, 'policies'))
  const mapActions = compact(map(items, 'actions'))
  const policies = parsePolicies([...new Set(concat([], ...mapPolicies))])
  const item: AutocompleteTable = {
    policies,
    policies_cnt: getCountPolicies(policies),
    total_actions: !isEmpty(mapTotalActions)
      ? {
          READ: Math.max(...mapTotalActions.map(o => o.READ)),
          WRITE: Math.max(...mapTotalActions.map(o => o.WRITE)),
          ADMIN: Math.max(...mapTotalActions.map(o => o.ADMIN))
        }
      : {},
    used_actions: !isEmpty(mapUsedActions)
      ? {
          READ: Math.max(...mapUsedActions.map(o => o.READ)),
          WRITE: Math.max(...mapUsedActions.map(o => o.WRITE)),
          ADMIN: Math.max(...mapUsedActions.map(o => o.ADMIN))
        }
      : {},
    actions: [...new Set(concat([], ...mapActions))]
  }
  if (item?.used_actions) {
    item.usedActions = map(item?.used_actions).join(', ')
  }
  if (item?.total_actions) {
    item.totalActions = map(item?.total_actions).join(', ')
  }
  if (!isEmpty(item?.actions)) {
    item.actions_str = parseActions(item)
  } else if (!isEmpty(item?.total_actions)) {
    item.actions_str = parseActionsFromObject(item?.total_actions)
  }
  return item
}

export function parsedItemByAccountInfo(
  item: SearchResultTableRow,
  account_info: AccountInfo[],
  name?: string
) {
  return {
    resource_account_name:
      item.resourceAccountID && account_info
        ? getAccountNameById(item.resourceAccountID, account_info)
        : '',
    identity_account_name:
      item.identityAccountID && account_info
        ? getAccountNameById(item.identityAccountID, account_info)
        : '',
    region_name: getRegionFromUri(name),
    resource_region_name: getRegionFromUri(item?.resource_uri),
    identity_region_name: getRegionFromUri(item?.identity_uri)
  } as SearchResult
}

export function formatSearchResultByField(
  arr: SearchResultTableRow[],
  byField: keyof SearchResult,
  account_info?: AccountInfo[]
): SearchResult[] {
  const grouped = chain(arr)
    .groupBy(byField)
    .map((items, key) => {
      const unionFields = unionBy(items, x => x[byField])[0]
      return {
        ...unionFields,
        ...parseCommonResourceFields(items),
        ...parsedItemByAccountInfo(unionFields, account_info, key),
        items
      } as SearchResult
    })
    .value()
  return grouped
}

export const getHumanNameByType = (
  type: ResourceTypeEnum,
  resourceTypes: ResourceTypeDict
) => resourceTypes[type]?.display_name || type
